<template>
  <ContentCard :loading="loadingUpdateAgency">
    <template #header>
      <ReviewsAgencyTabs />
    </template>
    <template #subheader>
      <ContentCardSubheader :back-to="{ name: ReviewsRouteName.ReviewsAgencyView }">
        {{ t('Reviews.agency.edit.industriesTitle') }}
        <template #actions>
          <button class="btn-secondary-color-s mr-2" data-testid="agency-industries-cancel-button" @click="onCancel">
            {{ t('Reviews.edit.action.cancel') }}
          </button>
          <button class="btn-primary-color-s" data-testid="agency-industries-save-button" @click="onSubmit">
            {{ t('Reviews.edit.action.save') }}
          </button>
        </template>
      </ContentCardSubheader>
    </template>
    <div class="pt-6">
      <IndustriesEditForm :industries="formData.industries" />
      <ServicesEditForm :categories="sortedCategories" :services="formData.services" />
    </div>
    <template #footer>
      <div class="my-6 flex flex-row items-center justify-end">
        <button class="btn-secondary-color-s mr-2" data-testid="agency-industries-foot-cancel" @click="onCancel">
          {{ t('Reviews.edit.action.cancel') }}
        </button>
        <button class="btn-primary-color-s" data-testid="agency-industries-foot-save" @click="onSubmit">
          {{ t('Reviews.edit.action.save') }}
        </button>
      </div>
    </template>
  </ContentCard>
  <EditCloseConfirmModal ref="confirmCloseModal" :back-to="backTo" />
</template>

<script setup lang="ts">
import { track } from '@ramp106/omrjs-tracking-reviews'
import { useVuelidate } from '@vuelidate/core'
import { v4 as uuidv4 } from 'uuid'
import { useTemplateRef, inject, reactive, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import EditCloseConfirmModal from '@/components/ReviewsManagement/EditCloseConfirmModal.vue'
import { type AgencyData, useUpdateReviewsAgencyMutation } from '@/gql/reviews'
import { showNotification } from '@/helpers/notificationHelper'
import { ReviewsRouteName } from '@/router/types'
import { reloadReviewsAgencyKey } from '@/symbols/reviewsReloadKeys'
import ContentCard from '@/ui/ContentCard.vue'
import ContentCardSubheader from '@/ui/ContentCardSubheader.vue'

import IndustriesEditForm from './IndustriesEditForm.vue'
import ServicesEditForm from './ServicesEditForm.vue'
import ReviewsAgencyTabs from '../ReviewsAgencyTabs.vue'
import type { FeatureFormData, IndustriesAndServicesFormData } from './types'

const props = defineProps<{
  agency: AgencyData
}>()

const reloadReviewsAgency = inject(reloadReviewsAgencyKey) as () => void

const { t } = useI18n()
const router = useRouter()
const confirmCloseModal = useTemplateRef('confirmCloseModal')

const backTo = { name: ReviewsRouteName.ReviewsAgencyViewIndustriesAndServices, params: { agencyId: props.agency.id } }

const sortedCategories = computed(() => [...props.agency.categories].sort((a, b) => a.id.localeCompare(b.id)))

const formData = reactive<IndustriesAndServicesFormData>({
  industries: [
    ...props.agency.assignedIndustries.map((assignedIndustry) => ({
      id: assignedIndustry.id ?? undefined, // TODO why this can be undefined?
      industryId: assignedIndustry.industry.id,
      position: assignedIndustry.position,
      _vid: uuidv4(),
    })),
  ],
  services: createFormCategoriesAndMapAssignedFeatures(),
})

function createFormCategoriesAndMapAssignedFeatures() {
  const reducedCategories = sortedCategories.value.reduce(
    (acc, category) => {
      acc[category.id] = []
      return acc
    },
    {} as Record<string, FeatureFormData[]>,
  )

  props.agency.assignedFeatures.forEach((feature) => {
    reducedCategories[feature.feature.category!.id].push({
      id: feature.id ?? undefined,
      position: feature.position,
      featureId: feature.feature.id,
      categoryId: feature.feature.category!.id,
      _vid: uuidv4(),
    })
  })
  return reducedCategories
}

const rules = computed(() => ({
  services: {},
  industries: {},
}))

const v$ = useVuelidate(rules, formData)

const {
  mutate: mutateAgency,
  loading: loadingUpdateAgency,
  onDone,
  onError,
} = useUpdateReviewsAgencyMutation({
  clientId: 'reviews',
})

function onCancel() {
  track('profile_update_cancel', { surface: 'editor', entity_type: 'service', entity_id: props.agency.id })
  if (v$.value.$anyDirty) {
    confirmCloseModal.value?.show()
  } else {
    router.push(backTo)
  }
}

async function onSubmit() {
  track('profile_update', { surface: 'editor', entity_type: 'service', entity_id: props.agency.id })
  v$.value.$touch()
  if (v$.value.$invalid) {
    showNotification(t('Reviews.agency.notifications.invalidData'), 'error')
    return
  }

  await mutateAgency({
    id: props.agency.id,
    attributes: {
      assignedIndustriesAttributes: formData.industries.map((industry) => ({
        _destroy: industry._destroy,
        id: industry.id,
        industryId: industry.industryId,
        position: industry.position,
      })),
      assignedFeaturesAttributes: Object.entries(formData.services).flatMap(([, features]) => {
        return features.map((feature) => ({
          _destroy: feature._destroy,
          id: feature.id,
          featureId: feature.featureId,
          position: feature.position,
        }))
      }),
    },
  })
}

onDone(() => {
  reloadReviewsAgency()
  showNotification(t('Reviews.agency.notifications.savedSuccessfully', { name: props.agency.title }), 'success')
  router.push(backTo)
})

onError((error) => showError(error))

const showError = (error: Error) => {
  if (error) showNotification(error.message, 'error')
}
</script>
